import { FormikContextType, FormikErrors } from 'formik';
import { generatePath } from 'react-router';

import { NotificationOption } from '~/components/Notification';
import { ROUTES } from '~/constants/routes';
import { RoundRef } from '~/pages/CreateCompany/components/GeneralInfo/components/Round';
import { CreateCompanyFormValues, CreateUserFormValues } from '~/types/formValues';
import { ApiError } from '~/types/models';
import { calculateRoundSummary } from '~/utils/calculations';
import { transformFormikErrorsForTouched } from '~/utils/transforms';

export const ceilToStep = (number: number, step: number) => {
  return Math.ceil(number * step) / step;
};

export const getApiError = (error: unknown) => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    if (typeof error.status === 'number') {
      const apiError = error as ApiError;

      return apiError;
    }
  }

  return null;
};

type RoundsType = CreateCompanyFormValues['form']['rounds'];
type SubroundsType = CreateCompanyFormValues['form']['rounds'][number]['subrounds'];

export const handleCompanyFormikErrors = async (
  formik: FormikContextType<CreateCompanyFormValues>,
  errors: FormikErrors<CreateCompanyFormValues>,
  showNotification: (option: NotificationOption) => void,
  roundRefs: React.RefObject<(RoundRef | null)[]>,
) => {
  const sectionsWithErrors: string[] = [];

  if (errors.form) {
    const generalInfoSectionFields = Object.keys(errors.form).filter(key => {
      const typedKey = key as keyof CreateCompanyFormValues['form'];

      return typedKey !== 'comments' && typedKey !== 'rounds' && !!errors.form?.[typedKey];
    });

    if (generalInfoSectionFields.length) {
      sectionsWithErrors.push('General Info');
    }

    if (errors.form.rounds) {
      const rounds = errors.form.rounds as FormikErrors<RoundsType>;

      rounds.forEach((round, roundIndex) => {
        if (round) {
          let expanded = false;
          const subrounds = round.subrounds as FormikErrors<SubroundsType>;

          subrounds.forEach((subround, subroundIndex) => {
            if (subround) {
              sectionsWithErrors.push(
                `Subround ${String.fromCharCode(65 + roundIndex)}${subroundIndex + 1}`,
              );

              if (!expanded) {
                expanded = true;

                roundRefs.current[roundIndex]?.expand(subroundIndex);
              }
            }
          });
        }
      });
    }
  }

  formik.values.form.rounds.forEach((round, index) => {
    const summary = calculateRoundSummary(round, formik.values.form.firstFinancingDate);

    if (summary.errors.preMoney) {
      const roundSectionWithErrors = sectionsWithErrors.filter(sectionName =>
        sectionName.startsWith(`Subround ${round.name}`),
      );

      if (roundSectionWithErrors.length === 0) {
        roundRefs.current[index]?.expand(round.subrounds.length);
      }

      sectionsWithErrors.push(`Round ${round.name} Summary`);
    }
  });

  if (sectionsWithErrors.length) {
    showNotification({
      type: 'error',
      title: 'Submit Draft',
      message: `Submission failed due to errors in the following section(s): ${sectionsWithErrors.join(
        ', ',
      )}.`,
    });
  }

  await formik.setTouched(transformFormikErrorsForTouched(errors));
};

export const handleUserFormikErrors = (
  errors: FormikErrors<CreateUserFormValues>,
  showNotification: (option: NotificationOption) => void,
) => {
  const fieldsWithError: string[] = [];

  Object.keys(errors).forEach(key => {
    const typedKey = key as keyof Omit<CreateUserFormValues, 'confirmPassword'>;

    if (errors[typedKey]) {
      let field = '';

      if (typedKey === 'firstName') {
        field = 'First Name';
      }

      if (typedKey === 'lastName') {
        field = 'Last Name';
      }

      if (typedKey === 'email') {
        field = 'Email';
      }

      if (typedKey === 'position') {
        field = 'Position';
      }

      if (typedKey === 'role') {
        field = 'Role';
      }

      if (typedKey === 'password') {
        field = 'Password';
      }

      fieldsWithError.push(field);
    }
  });

  if (fieldsWithError.length) {
    showNotification({
      type: 'error',
      title: 'Submit User',
      message: `Submission failed due to errors in the following fields(s): ${fieldsWithError.join(
        ', ',
      )}.`,
    });
  }
};

export const generateLinkUrl = (linkUuid: string, companyId: number) => {
  return `${window.location.origin}${generatePath(ROUTES.EDIT_COMPANY, { id: companyId.toString() })}?link=${linkUuid}`;
};

export const capitalize = (word: string) => {
  const firstLetter = word[0].toUpperCase();

  return firstLetter + word.slice(1);
};
